import React, { useState, useEffect, useRef } from 'react';
import { assetUrl } from '../../utils/assets';
import { Card, DeckStatus, SuggestedPhrases, EffectStatus } from '../../types';
import CardGrid from './CardGrid';
import DeckPile from './DeckPile';
import CardDetailModal from './CardDetailModal';
import ParadoxCardDetail from './ParadoxCardDetail';
import ActiveEffectsDisplay from '../ActiveEffectsDisplay';
import { useSoundEffects } from '../../context/SoundEffectsContext';
import './FullScreenCardMode.css';

interface FullScreenCardModeProps {
  deckStatus: DeckStatus;
  suggestedPhrases: SuggestedPhrases;
  isLoading: boolean;
  onSubmitCardAction: (cardName: string, actionPhrase: string) => void;
  onBackToStory: () => void;
  isInFreeFormStep?: boolean;
  isTutorialMode?: boolean;
  tutorialStep?: number;
  onCardPlayModeChange?: (isInCardPlayMode: boolean) => void;
  onTypingComplete?: () => void;
  isAnimatingAsterisk?: boolean;
  activeEffects?: EffectStatus[];
  onParadoxPlay?: (cardName: string) => Promise<void>;
  isParadoxLoading?: boolean;
}

const FullScreenCardMode: React.FC<FullScreenCardModeProps> = ({
  deckStatus,
  suggestedPhrases,
  isLoading,
  onSubmitCardAction,
  onBackToStory,
  isInFreeFormStep = true,
  isTutorialMode = false,
  tutorialStep,
  onCardPlayModeChange,
  onTypingComplete,
  isAnimatingAsterisk = false,
  activeEffects = [],
  onParadoxPlay,
  isParadoxLoading = false,
}) => {
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);
  const [cardBeingPlayed, setCardBeingPlayed] = useState<Card | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const { playSound, playMultipleSounds } = useSoundEffects();
  const hasPlayedDealSound = useRef(false);
  const wasAnimating = useRef(false);

  useEffect(() => {
    requestAnimationFrame(() => {
      setIsVisible(true);
    });
  }, []);

  // Play deal_card sounds when cards appear
  useEffect(() => {
    if (!isLoading && deckStatus.hand.length > 0 && !hasPlayedDealSound.current) {
      playMultipleSounds('deal_card', deckStatus.hand.length, 400);
      hasPlayedDealSound.current = true;
    }
  }, [isLoading, deckStatus.hand.length, playMultipleSounds]);

  // Close modal after animation completes
  useEffect(() => {
    if (wasAnimating.current && !isAnimatingAsterisk) {
      // Animation just finished - close the modal
      setCardBeingPlayed(null);
      setSelectedCard(null);
      if (onCardPlayModeChange) {
        onCardPlayModeChange(false);
      }
    }
    wasAnimating.current = isAnimatingAsterisk;
  }, [isAnimatingAsterisk, onCardPlayModeChange]);

  const handleCardClick = (card: Card) => {
    if (isLoading) return;
    playSound('card_flip');
    setSelectedCard(card);
    // Notify that user is now in action selection mode
    if (onCardPlayModeChange) {
      onCardPlayModeChange(true);
    }
  };

  const handleCloseDetail = () => {
    // Don't allow closing during animation
    if (isAnimatingAsterisk) return;
    setSelectedCard(null);
    setCardBeingPlayed(null);
    // Notify that user exited action selection mode
    if (onCardPlayModeChange) {
      onCardPlayModeChange(false);
    }
  };

  const handlePlayCard = (cardName: string, actionPhrase: string) => {
    // Store the card being played so we can show it during animation
    setCardBeingPlayed(selectedCard);
    onSubmitCardAction(cardName, actionPhrase);
    // Don't close modal here - let the animation complete first
    // The useEffect watching isAnimatingAsterisk will handle closing
  };

  const handleParadoxPlay = async (cardName: string) => {
    if (onParadoxPlay) {
      await onParadoxPlay(cardName);
      // After playing, close the detail view and return to card grid
      setSelectedCard(null);
      if (onCardPlayModeChange) {
        onCardPlayModeChange(false);
      }
    }
  };

  const isParadoxOrJunkCard = (card: Card): boolean => {
    return card.type === 'paradox' || card.type === 'junk';
  };

  const handleBackToStory = () => {
    setIsVisible(false);
    setTimeout(onBackToStory, 300);
  };

  return (
    <div className={`full-screen-card-mode ${isVisible ? 'visible' : ''}`}>
      <div className="full-screen-card-mode-top-bar">
        <button className="full-screen-card-mode-back" onClick={handleBackToStory}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6,9 12,15 18,9" />
          </svg>
          <span>Back to Story</span>
        </button>
        {!isLoading && (
          <div className="full-screen-card-mode-top-header">
            <h2>What do you do?</h2>
            <p>Tap a card to see your options</p>
          </div>
        )}
      </div>

      <div className="full-screen-card-mode-content">
        {isLoading ? (
          <div className="full-screen-card-mode-loading">
            <img
              src={assetUrl('/assets/ui/loading_story.png')}
              alt="Loading"
              className="full-screen-card-mode-loading-image"
            />
            <p>Loading next section...</p>
          </div>
        ) : (
          <CardGrid
            cards={deckStatus.hand}
            onCardClick={handleCardClick}
            disabled={isLoading}
          />
        )}
      </div>

      <div className="full-screen-card-mode-bottom-bar">
        <DeckPile type="draw" count={deckStatus.draw_pile.length} />
        {activeEffects.length > 0 && (
          <ActiveEffectsDisplay effects={activeEffects} variant="compact" />
        )}
        <DeckPile type="discard" count={deckStatus.discard_pile.length} />
      </div>

      {(selectedCard || cardBeingPlayed) && (
        isParadoxOrJunkCard((cardBeingPlayed || selectedCard)!) ? (
          <ParadoxCardDetail
            card={(cardBeingPlayed || selectedCard)!}
            activeEffects={activeEffects}
            onPlayCard={handleParadoxPlay}
            onBack={handleCloseDetail}
            isLoading={isParadoxLoading}
          />
        ) : (
          <CardDetailModal
            card={(cardBeingPlayed || selectedCard)!}
            suggestedPhrases={suggestedPhrases}
            isLoading={isLoading}
            onClose={handleCloseDetail}
            onPlayCard={handlePlayCard}
            isInFreeFormStep={isInFreeFormStep}
            isTutorialMode={isTutorialMode}
            tutorialStep={tutorialStep}
            onTypingComplete={onTypingComplete}
            isAnimatingAsterisk={isAnimatingAsterisk}
          />
        )
      )}
    </div>
  );
};

export default FullScreenCardMode;
